import { useMemo, useState } from "react";
import type { Draft, Exercise } from "@jackedlogs/shared";
import {
  buildIntensityGridFromDraft,
  hardSetsByMuscleFromDraft,
} from "@jackedlogs/shared";

import type { useTheme } from "../../Logger/theme";
import { useWeekSwipe } from "./useWeekSwipe";
import { useReducedMotion } from "../../useReducedMotion";
import type { PrescribeView } from "./PrescribeViewTabs";

export type PrescribeTreeViewArgs = {
  programDraft: Draft;
  exerciseCatalog: ReadonlyArray<Exercise> | undefined;
  theme: ReturnType<typeof useTheme>;
  selectedWeek: number;
  setSelectedWeek: (next: number) => void;
};

/** What PrescribeTree derives before it renders: the two read-only grids,
 *  which tab is showing, and the week deck.
 *
 *  Extracted from PrescribeTree at parity (CN-prescribe-tree-split) while it
 *  sat at 490/500 — a cold cut, before PE-37/38 arrive and the cap is the
 *  thing forcing the seam instead of the seam being chosen.
 *
 *  ⛔ Only the JSX-free half moved. `renderUnplaced`, the placement seam it
 *  reads and the language chip stayed behind: a .ts hook cannot hold JSX, and
 *  splitting a render function from the values it closes over to satisfy a
 *  line count is how a file gets smaller and harder to read at the same time.
 */
export function usePrescribeTreeView({
  programDraft,
  exerciseCatalog,
  theme,
  selectedWeek,
  setSelectedWeek,
}: PrescribeTreeViewArgs) {
  const intensityGrid = useMemo(
    () => buildIntensityGridFromDraft(programDraft),
    [programDraft],
  );

  const hardSetsByMuscle = useMemo(
    () => hardSetsByMuscleFromDraft(programDraft, exerciseCatalog ?? []),
    [programDraft, exerciseCatalog],
  );

  // PE-27 — which of the two tabs is showing. Defaults to Matrice, the leftmost
  // tab: it matches the locked mock (program-editor-mock.html: state.tab starts
  // "matrix") and keeps the default-selected tab aligned with a left-to-right
  // scan. The card and the two read-only grids are mutually exclusive here so
  // the tall card never scrolls past the grids — the defect Antenore censused.
  const [view, setView] = useState<PrescribeView>("matrice");

  // ⛔ A module StyleSheet cannot see the theme, and a sticky row without an
  // opaque fill lets the card scroll straight through the tabs. The colour has
  // to come from the token, at the callsite that has it.
  const tabsStickyStyle = useMemo(
    () => ({ backgroundColor: theme.palette.bg }),
    [theme],
  );

  const reducedMotion = useReducedMotion();
  const weekDeck = useWeekSwipe({
    week: selectedWeek,
    weekCount: programDraft.cycleWeeks,
    onSelectWeek: setSelectedWeek,
    reducedMotion,
  });

  // PE-31 — the week cursor lives in the card's own action bar now, between the
  // superset affordance and ⇄/❓/🗑 (Antenore, 29/08). It is a CONTROL and not a
  // readout because the swipe reaches neither a screen reader nor a keyboard,
  // and the arrows that used to carry that job are gone.
  const weekCue = useMemo(
    () => ({
      index: selectedWeek,
      count: programDraft.cycleWeeks,
      onNext: () =>
        setSelectedWeek((selectedWeek + 1) % programDraft.cycleWeeks),
      // ⭐ Wraps the other way, and it is not decoration: a swipe reaches
      // neither a screen reader nor a keyboard, so without a way BACK the one
      // door this fetta built would cost eleven taps to undo one on a 12-week
      // cycle (a11y review). It shows nothing — the cue carries it as a hold
      // and a named action.
      onPrev: () =>
        setSelectedWeek(
          (selectedWeek - 1 + programDraft.cycleWeeks) % programDraft.cycleWeeks,
        ),
    }),
    [selectedWeek, programDraft.cycleWeeks, setSelectedWeek],
  );

  return {
    intensityGrid,
    hardSetsByMuscle,
    view,
    setView,
    tabsStickyStyle,
    weekDeck,
    weekCue,
  };
}
